import { NavLink, useParams, useLocation } from "react-router";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  BookOpen,
  Gift,
  Skull,
  CalendarDays,
  MessageCircle,
  Plus,
  UserPlus,
  UserCog,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useApi } from "@/hooks/useApi";

interface SidebarProps {
  role: "admin" | "client";
  jwt: string;
}

const clientSubNavItems = (jwt: string, clientId: string) => [
  { to: `/admin/${jwt}/${clientId}/chores`, icon: ListChecks, label: "Chores" },
  { to: `/admin/${jwt}/${clientId}/rules`, icon: BookOpen, label: "Rules" },
  { to: `/admin/${jwt}/${clientId}/rewards`, icon: Gift, label: "Rewards" },
  { to: `/admin/${jwt}/${clientId}/punishments`, icon: Skull, label: "Punishments" },
  { to: `/admin/${jwt}/${clientId}/sessions`, icon: CalendarDays, label: "Sessions" },
  { to: `/admin/${jwt}/${clientId}/messages`, icon: MessageCircle, label: "Messages" },
];

const clientNavItems = (jwt: string) => [
  { to: `/client/${jwt}/dashboard`, icon: LayoutDashboard, label: "Dashboard" },
  { to: `/client/${jwt}/chores`, icon: ListChecks, label: "Chores" },
  { to: `/client/${jwt}/rules`, icon: BookOpen, label: "Rules" },
  { to: `/client/${jwt}/sessions`, icon: CalendarDays, label: "Sessions" },
  { to: `/client/${jwt}/messages`, icon: MessageCircle, label: "Messages" },
  { to: `/client/${jwt}/profile`, icon: UserCog, label: "Profile" },
];

export function Sidebar({ role, jwt }: SidebarProps) {
  const { clientId } = useParams();
  const location = useLocation();

  const { data: clients } = useApi<Array<{ _key: string; name: string; icon: string; points: number }>>(
    role === "admin" ? `/api/admin/${jwt}/clients` : null
  );

  // Check if a client path is active (either the client manage page or any sub-page)
  const isClientActive = (key: string) => {
    return clientId === key;
  };

  // Check if we're on the exact client manage page (not a sub-section)
  const isClientManagePage = (key: string) => {
    return location.pathname === `/admin/${jwt}/${key}`;
  };

  if (role === "client") {
    const items = clientNavItems(jwt);
    return (
      <aside className="hidden md:flex flex-col w-14 lg:w-56 shrink-0 border-r border-app-border bg-app-surface">
        {/* Logo */}
        <div className="h-14 flex items-center justify-center lg:justify-start px-3 lg:px-4 border-b border-app-border">
          <h1 className="text-heading font-bold hidden lg:block">✨ ChoreApp</h1>
          <span className="text-xl lg:hidden">✨</span>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-1.5 lg:p-2 space-y-0.5">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-2.5 lg:px-3 py-2 rounded-lg transition-all duration-150 text-body",
                  isActive
                    ? "bg-app-primary text-white shadow-sm"
                    : "text-app-text hover:bg-app-surface-raised"
                )
              }
            >
              <item.icon className="w-5 h-5 shrink-0 mx-auto lg:mx-0" />
              <span className="hidden lg:inline">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    );
  }

  // Admin sidebar
  return (
    <aside className="hidden md:flex flex-col w-14 lg:w-56 shrink-0 border-r border-app-border bg-app-surface">
      {/* Logo */}
      <div className="h-14 flex items-center justify-center lg:justify-start px-3 lg:px-4 border-b border-app-border">
        <h1 className="text-heading font-bold hidden lg:block">✨ ChoreApp</h1>
        <span className="text-xl lg:hidden">✨</span>
      </div>

      {/* Dashboard link */}
      <div className="p-1.5 lg:p-2">
        <NavLink
          to={clientId ? `/admin/${jwt}/${clientId}` : `/admin/${jwt}/dashboard`}
          end
          title="Dashboard"
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-2.5 lg:px-3 py-2 rounded-lg transition-all duration-150 text-body",
              isActive
                ? "bg-app-primary text-white shadow-sm"
                : "text-app-text hover:bg-app-surface-raised"
            )
          }
        >
          <LayoutDashboard className="w-5 h-5 shrink-0 mx-auto lg:mx-0" />
          <span className="hidden lg:inline">Dashboard</span>
        </NavLink>
      </div>

      {/* Clients section divider */}
      <div className="px-3 lg:px-4 pt-2 pb-1">
        <div className="hidden lg:flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-app-text-muted">Partners</span>
          <div className="flex-1 h-px bg-app-border" />
        </div>
        <div className="lg:hidden h-px bg-app-border mx-1" />
      </div>

      {/* Scrollable client list */}
      <div className="overflow-y-auto flex-1 px-1.5 lg:px-2 space-y-0.5">
        {clients?.map((client) => {
          const active = isClientActive(client._key);
          const onManagePage = isClientManagePage(client._key);

          return (
            <div key={client._key}>
              {/* Client entry */}
              <NavLink
                to={`/admin/${jwt}/${client._key}`}
                end
                title={client.name}
                className={() =>
                  cn(
                    "flex items-center gap-2.5 px-2 lg:px-3 py-1.5 rounded-lg transition-all duration-150 text-body",
                    active
                      ? "bg-app-primary/10 border-l-2 border-app-primary text-app-text font-medium"
                      : "text-app-text hover:bg-app-surface-raised border-l-2 border-transparent"
                  )
                }
              >
                <span className="text-base shrink-0 mx-auto lg:mx-0">{client.icon}</span>
                <span className="hidden lg:inline truncate text-sm">{client.name}</span>
              </NavLink>

              {/* Sub-nav for active client */}
              {active && (
                <div className="mt-0.5 mb-1 space-y-0.5">
                  {clientSubNavItems(jwt, client._key).map((sub) => (
                    <NavLink
                      key={sub.to}
                      to={sub.to}
                      title={sub.label}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2 pl-4 lg:pl-8 pr-2 py-1.5 rounded-lg transition-all duration-150 text-info",
                          isActive
                            ? "bg-app-primary text-white shadow-sm"
                            : "text-app-text-muted hover:bg-app-surface-raised hover:text-app-text"
                        )
                      }
                    >
                      <sub.icon className="w-4 h-4 shrink-0 mx-auto lg:mx-0" />
                      <span className="hidden lg:inline">{sub.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Client button */}
      <div className="p-1.5 lg:p-2 border-t border-app-border space-y-0.5">
        <NavLink
          to={`/admin/${jwt}/profile`}
          title="Profile"
          className={({ isActive }) =>
            cn(
              "flex items-center justify-center lg:justify-start gap-2 px-2.5 lg:px-3 py-2 rounded-lg transition-all duration-150 text-body",
              isActive
                ? "bg-app-primary text-white shadow-sm"
                : "text-app-text-muted hover:bg-app-surface-raised hover:text-app-text"
            )
          }
        >
          <UserCog className="w-5 h-5 shrink-0" />
          <span className="hidden lg:inline text-sm font-medium">Profile</span>
        </NavLink>
        <NavLink
          to={`/admin/${jwt}/clients/new`}
          title="Add Partner"
          className={({ isActive }) =>
            cn(
              "flex items-center justify-center lg:justify-start gap-2 px-2.5 lg:px-3 py-2 rounded-lg transition-all duration-150 text-body",
              isActive
                ? "bg-app-primary text-white shadow-sm"
                : "text-app-primary hover:bg-app-primary/10"
            )
          }
        >
          <UserPlus className="w-5 h-5 shrink-0" />
          <span className="hidden lg:inline text-sm font-medium">Add Partner</span>
        </NavLink>
      </div>
    </aside>
  );
}
